import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Logout from '@mui/icons-material/Logout';
import TuneIcon from '@mui/icons-material/Tune';
import {Divider, ListItem, ListItemIcon, ListItemText, MenuItem} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';
import {AdministrationDialog} from '../admin/AdministrationDialog';
import {useTranslation} from '../i18n/hooks/useTranslation';
import {Translation} from '../i18n/Translation';
import {DropDownMenu} from '../navigation/DropDownMenu';
import {MenuItemLink} from '../navigation/MenuItemLink';
import {AppInstallMenuItem} from '../pwa/AppInstallMenuItem';
import {ComponentWithoutChildren} from '../react/types';
import {Paths} from '../routes/paths';
import {CurrentUserAvatar} from './CurrentUserAvatar';
import {EditUserDialog} from './EditUserDialog';
import {useCurrentUser} from './hooks/useCurrentUser';
import {hasPermission} from '#common/users/permissions';

export const UserMenu: ComponentWithoutChildren = () => {
  const [administrationDialogOpen, setAdministrationDialogOpen] = useState<boolean>(false);
  const [editUserDialogOpen, setEditUserDialogOpen] = useState<boolean>(false);

  const openAdministrationDialog = useCallback(() => setAdministrationDialogOpen(true), [setAdministrationDialogOpen]);
  const closeAdministrationDialog = useCallback(
    () => setAdministrationDialogOpen(false),
    [setAdministrationDialogOpen],
  );

  const openUserDialog = useCallback(() => setEditUserDialogOpen(true), [setEditUserDialogOpen]);
  const closeUserDialog = useCallback(() => setEditUserDialogOpen(false), [setEditUserDialogOpen]);

  const currentUser = useCurrentUser();
  const {translate} = useTranslation('user');
  const user = currentUser.get();
  const {email, name} = user;

  const canAdminister = useMemo(() => hasPermission(user, 'list:signup.rule'), [user]);

  return (
    <>
      <DropDownMenu label={translate('user:userMenu')} title={<CurrentUserAvatar />} variant="icon">
        <ListItem>
          <ListItemText primary={name} secondary={email} slotProps={{primary: {sx: {fontWeight: 'bolder'}}}} />
        </ListItem>
        <Divider />
        <AppInstallMenuItem />
        <MenuItem onClick={openUserDialog}>
          <ListItemIcon>
            <TuneIcon />
          </ListItemIcon>
          <Translation keys="user:preferences">{(text) => <ListItemText primary={text} />}</Translation>
        </MenuItem>
        {canAdminister && (
          <MenuItem onClick={openAdministrationDialog}>
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <Translation keys="user:administration">{(text) => <ListItemText primary={text} />}</Translation>
          </MenuItem>
        )}
        <Translation keys="auth:logout">
          {(text) => (
            <MenuItemLink to={Paths.Logout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              {text}
            </MenuItemLink>
          )}
        </Translation>
      </DropDownMenu>
      <EditUserDialog handleClose={closeUserDialog} open={editUserDialogOpen} />
      <AdministrationDialog handleClose={closeAdministrationDialog} open={administrationDialogOpen} user={user} />
    </>
  );
};
